import { useState, useEffect } from 'react';
import { useNavigate, Link, Outlet } from 'react-router-dom';
import { LayoutDashboard, Users, Settings, Mail, FileText, Radio, BarChart3, Truck, MessageSquare, TrendingUp, Activity, LogOut, Menu, X, Clock, ShieldCheck } from 'lucide-react';
import axios from 'axios';
import { useInactivityLogout } from '../../hooks/useInactivityLogout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function getToken() {
  return localStorage.getItem('admin_token');
}

export function adminAxios() {
  return axios.create({
    baseURL: BACKEND_URL,
    headers: { Authorization: `Bearer ${getToken()}` },
  });
}

export default function AdminLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const { showWarning, stayLoggedIn, logout: autoLogout } = useInactivityLogout();

  useEffect(() => {
    if (!getToken()) navigate('/admin');
  }, [navigate]);

  const logout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_email');
    localStorage.removeItem('rts_last_activity');
    navigate('/admin');
  };

  const navItems = [
    { label: 'Executive', icon: TrendingUp, path: '/admin/executive' },
    { label: 'Dashboard', icon: LayoutDashboard, path: '/admin/dashboard' },
    { label: 'Clients', icon: Users, path: '/admin/clients' },
    { label: 'Offers', icon: Mail, path: '/admin/offers' },
    { label: 'Quotes', icon: MessageSquare, path: '/admin/quotes' },
    { label: 'Compliance', icon: ShieldCheck, path: '/admin/compliance' },
    { label: 'Analytics', icon: BarChart3, path: '/admin/analytics' },
    { label: 'Campaigns', icon: Activity, path: '/admin/campaign-diagnostics' },
    { label: 'Suppliers', icon: Truck, path: '/admin/suppliers' },
    { label: 'Documents', icon: FileText, path: '/admin/documents' },
    { label: 'Broadcasts', icon: Radio, path: '/admin/broadcasts' },
    { label: 'Settings', icon: Settings, path: '/admin/settings' },
  ];

  return (
    <div className="min-h-screen flex bg-slate-50" data-testid="admin-layout">
      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-slate-900 text-white transform transition-transform lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="p-6 border-b border-slate-700">
          <h2 className="font-outfit text-lg font-bold">Real Tech Admin</h2>
          <p className="font-manrope text-xs text-slate-400 mt-1">{localStorage.getItem('admin_email')}</p>
        </div>
        <nav className="p-4 space-y-1">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setSidebarOpen(false)}
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-300 hover:bg-slate-800 hover:text-white transition-colors font-manrope text-sm"
              data-testid={`admin-nav-${item.label.toLowerCase()}`}
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </Link>
          ))}
        </nav>
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-700">
          <button
            onClick={logout}
            className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-400 hover:bg-slate-800 hover:text-white transition-colors font-manrope text-sm w-full"
            data-testid="admin-logout-button"
          >
            <LogOut className="w-5 h-5" />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="bg-white border-b border-slate-200 px-6 py-4 flex items-center gap-4">
          <button className="lg:hidden" onClick={() => setSidebarOpen(true)}>
            <Menu className="w-6 h-6 text-slate-600" />
          </button>
          <h1 className="font-outfit text-xl font-semibold text-slate-900">Admin Portal</h1>
        </header>
        <main className="flex-1 p-6 overflow-auto">
          <Outlet />
        </main>
      </div>

      {/* Inactivity Warning Modal */}
      {showWarning && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60">
          <div className="bg-white rounded-2xl w-full max-w-sm p-6 m-4 shadow-2xl text-center">
            <Clock className="w-12 h-12 text-amber-500 mx-auto mb-3" />
            <h3 className="font-outfit text-lg font-bold text-slate-900 mb-2">Session Expiring</h3>
            <p className="font-manrope text-sm text-slate-600 mb-4">Your session will expire in 5 minutes due to inactivity.</p>
            <div className="flex gap-3 justify-center">
              <button onClick={stayLoggedIn} className="px-5 py-2.5 rounded-xl bg-[#0096D6] text-white font-manrope text-sm font-semibold">Stay Logged In</button>
              <button onClick={function() { autoLogout('MANUAL'); }} className="px-5 py-2.5 rounded-xl border border-slate-200 font-manrope text-sm text-slate-600">Logout</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
